import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { MIN_CELL_WIDTH } from '@components/VirtualMasonry/masonryConstants';
import { VirtualMasonry, VirtualMasonryCellProps } from '@components/VirtualMasonry/VirtualMasonry';
import { createDebugMediaCardFiles } from '@lib/debug/libraryDebugData';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { observer } from 'mobx-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { MediaCard } from '../MediaCard/MediaCard';

export const DebugMediaCardGrid = observer(() => {
  const folder = useLibraryFolder();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [masonryWidth, setMasonryWidth] = useState(0);

  // Use a callback ref so the ResizeObserver attaches whenever the div mounts,
  // even if it wasn't present on the initial render (e.g. data still loading).
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (node) {
      const updateWidth = () => {
        const containerWidth = node.getBoundingClientRect().width;
        // Subtract scrollbar width so the masonry doesn't overflow horizontally
        setMasonryWidth(containerWidth - 20);
      };
      updateWidth();
      resizeObserverRef.current = new ResizeObserver(updateWidth);
      resizeObserverRef.current.observe(node);
    }
  }, []);

  const completedMedia = useMemo(
    () => folder.files.filter((f) => f.status === 'completed'),
    [folder.files],
  );

  const debugFiles = useMemo(() => createDebugMediaCardFiles(completedMedia), [completedMedia]);

  const cells = debugFiles.map((file) => {
    const w = file.metadata?.video?.width;
    const h = file.metadata?.video?.height;
    let cardShape: 'tall' | 'short' = 'short';
    if (w && h) {
      const ratio = w / h;
      if (ratio < 0.85 || ratio > 1.15) cardShape = h > w ? 'tall' : 'short';
    }
    return {
      key: file.debugKey,
      cardShape,
      children: ({ isVisible, width: cellWidth }: VirtualMasonryCellProps) => (
        <MediaCard file={file} isVisible={isVisible} cellWidth={cellWidth} />
      ),
    };
  });

  if (completedMedia.length === 0) {
    return (
      <div style={{ padding: 24, color: 'rgba(255,255,255,0.6)' }}>
        No completed media in this library. Upload and process at least one file to use debug media
        cards.
      </div>
    );
  }

  return (
    <GradientScrollDiv style={{ width: '100%', height: '100%' }} ref={scrollContainerRef}>
      <div ref={containerRef}>
        <VirtualMasonry
          width={masonryWidth}
          minCellWidth={MIN_CELL_WIDTH}
          masonryCellGap={16}
          cells={cells}
          addBorder={false}
          clipCells={false}
          scrollContainerRef={scrollContainerRef}
        />
      </div>
    </GradientScrollDiv>
  );
});
